import React, { useEffect } from 'react';
import { Layer, Line, Rect, Text, Transformer } from "react-konva";

export default function Grid(props) {
  // console.log(props.grid, "Grid props");
  const trRef = React.useRef();
  const shapeRef = React.useRef(); // single ref for shape + transformer
  const [gridResults, setGridResults] = React.useState([]);
  const worldHeightValue = (Number.isFinite(props.worldHeight) && props.worldHeight > 0)
    ? props.worldHeight
    : (Number(props.grid?.origin?.y?.length) || 0);
  const shouldFlipY = Boolean(props.flipY && worldHeightValue > 0);
  const toWorldY = (y) => (shouldFlipY ? (worldHeightValue - y) : y);
  const toWorldX = (x) => x;
  const scaleX = props.gridScaleX * props.dxfScale;
  const scaleY = props.gridScaleY * props.dxfScale;
  const safeStageScale = (Number.isFinite(props.stageScale) && props.stageScale > 0)
    ? props.stageScale
    : 1;
  const getScaledLineWidth = (baseWidth = 1, minWidth = 0.001) =>
    Math.max(minWidth, baseWidth / safeStageScale);
  useEffect(() => {
    if (props.selected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    } else if (trRef.current) {
      trRef.current.nodes([]);
    }
  }, [props.selected]);

  useEffect(() => {
    console.log("useEffect in grid", props.areaResults);
    if (!props.areaResults || props.areaResults.length === 0) {
      setGridResults([]);
      return;
    }
    for (var i = 0; i < props.areaResults.length; i++) {
      var item = props.areaResults[i];
      if (item.calcResults?.outdoorResults[0]?.gridId == props.i) {
        setGridResults(item.calcResults?.outdoorResults[0]?.contourResults);
        break;
      }
    }
  }, [props.areaResults])


  const drawResults = (ctx, shape) => {
    if (!gridResults || gridResults.length === 0) return;

    const grid = props.grid;
    const toFinite = (value, fallback) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    // Visual dimensions (already scaled by gridScale & dxfScale via shape props)
    const width = shape.width();
    const height = shape.height();
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

    // Convert spacing from logical units -> pixels (same scale factors used for width/height construction)
    const xSpacingPxRaw = toFinite(grid.xSpacing, 1.5) * toFinite(props.gridScaleX, 1) * toFinite(props.dxfScale, 1);
    const ySpacingPxRaw = toFinite(grid.ySpacing, 1.5) * toFinite(props.gridScaleY, 1) * toFinite(props.dxfScale, 1);
    const xSpacingPx = Math.max(0.5, Math.abs(xSpacingPxRaw));
    const ySpacingPx = Math.max(0.5, Math.abs(ySpacingPxRaw));

    let xPoints = Math.floor(width / xSpacingPx);
    let yPoints = Math.floor(height / ySpacingPx);
    if (xPoints * xSpacingPx > width) xPoints--;
    if (yPoints * ySpacingPx > height) yPoints--;
    xPoints = Math.max(1, xPoints);
    yPoints = Math.max(1, yPoints);

    // Avoid pathological overdraw when spacing arrives invalid before settings are saved.
    const maxCells = 60000;
    const totalCells = xPoints * yPoints;
    if (totalCells > maxCells) {
      const reduction = Math.sqrt(totalCells / maxCells);
      xPoints = Math.max(1, Math.floor(xPoints / reduction));
      yPoints = Math.max(1, Math.floor(yPoints / reduction));
    }
    const userFontSize = toFinite(grid.gridValueFontSize, 8) / 16;

    // Base font in world units
    let baseFont = Number.isFinite(userFontSize)
      ? userFontSize * props.dxfScale
      : 3 * props.dxfScale;

    // Clamp to prevent microscopic fonts
    baseFont = Math.max(0.5, baseFont);

    // Optional upper clamp to stop giant fonts
    baseFont = Math.min(baseFont, 48);


    ctx.font = `${baseFont}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const dataMax = Math.max(...gridResults);
    const dataMin = props.minResult || 1;

    // Draw grid lines (optional thin)
    ctx.save();
    ctx.strokeStyle = "transparent"; // Was #aaa previously
    ctx.lineWidth = getScaledLineWidth(0.2, 0.05);
    for (let x = 0; x < xPoints; x++) {
      const xPos = x * (width / xPoints);
      ctx.beginPath();
      ctx.moveTo(xPos, 0);
      ctx.lineTo(xPos, height);
      ctx.stroke();
    }
    for (let y = 0; y < yPoints; y++) {
      const yPos = y * (height / yPoints);
      ctx.beginPath();
      ctx.moveTo(0, yPos);
      ctx.lineTo(width, yPos);
      ctx.stroke();
    }
    ctx.restore();

    const absRot = shape.getAbsoluteRotation();

    let point = 0;
    let drawXPoints = xPoints;
    let drawYPoints = yPoints;
    if (props.grid.edgeStart) {
      drawXPoints++;
      drawYPoints++;
    }

    const drawCellWidth = width / xPoints;
    const drawCellHeight = height / yPoints;

    for (let y = 0; y < drawYPoints; y++) {
      for (let x = 0; x < drawXPoints; x++) {
        if (point >= gridResults.length) break;
        const value = gridResults[point];
        if (value === undefined || value === null) {
          point++;
          continue;
        }
        if (value === dataMax) ctx.fillStyle = 'darkgreen';
        else if (value < dataMin) ctx.fillStyle = 'red';
        else ctx.fillStyle = 'cyan';

        let centerX, centerY;
        if (props.grid.edgeStart) {
          centerX = x * drawCellWidth;
          centerY = y * drawCellHeight;
        } else {
          centerX = (x * drawCellWidth) + (drawCellWidth / 2);
          centerY = (y * drawCellHeight) + (drawCellHeight / 2);
        }
        if (shouldFlipY) {
          centerY = height - centerY;
        }
        // Keep text upright: counter-rotate each label about its own center
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.scale(1, -1);

        if (absRot) ctx.rotate(-absRot * Math.PI / 180);
        ctx.fillText(Number(value).toFixed(2), 0, 0);
        ctx.restore();
        point++;
      }
    }
  };



  const getDynamicFontSize = () => {
    const safeScale = Math.max(0.1, Number(props.stageScale) || 1);
    return Math.max(0.22, 2.6 / safeScale);
  };

  const getTextPosition = () => {
    const fontSize = getDynamicFontSize();
    const inset = Math.max(0.2, fontSize * 0.2);

    if (!shapeRef.current) return { x: 0, y: 0 };

    const node = shapeRef.current;
    const nodeX = node.x();
    const nodeY = node.y();
    const nodeHeight = node.height();
    const visualTopY = shouldFlipY ? (nodeY + nodeHeight) : nodeY;

    return {
      x: nodeX + inset,
      y: props.textUnflipY ? (visualTopY + inset) : (visualTopY - fontSize - inset),
    };
  };


  return props.grid.id !== 0 ? (
    <Layer key={props.i}>

      {props.grid.name && props.grid.visible && (
        <Text
          text={props.grid.name && props.grid.name.length > 30 ? `${props.grid.name.slice(0, 25)}...` : props.grid.name}
          x={getTextPosition().x}
          y={getTextPosition().y}
          align="left"
          fontSize={getDynamicFontSize()}
          fontStyle="bold"
          fill="black"
          scaleY={props.textUnflipY ? -1 : 1}
          offsetY={props.textUnflipY ? getDynamicFontSize() : 0}
        />
      )}

      {/* {props.grid.type !== 'Vertical' ? ( */}
      <Rect
        ref={shapeRef}
        id={`grid-shape-${props.grid.id}`}
        name={`grid-shape grid-shape-${props.grid.id}`}
        x={props.grid.origin.x.start * props.gridScaleX * props.dxfScale}
        y={(props.flipY && Number.isFinite(props.worldHeight))
          ? (props.worldHeight - Number(props.grid.origin.y.start)) * props.gridScaleY * props.dxfScale
          : props.grid.origin.y.start * props.gridScaleY * props.dxfScale}
        width={props.grid.origin.x.length * props.gridScaleX * props.dxfScale}
        height={props.grid.origin.y.length * props.gridScaleY * props.dxfScale}
        fill={"transparent"}
        listening={!props.grid.locked}
        draggable={props.selected}
        draggableCursor="move"
        rotation={props.grid.angle || 0}
        sceneFunc={(ctx, shape) => {
          const isVerticalGrid = String(props.grid?.type || "").toLowerCase() === "vertical";
          if (props.showResults && !isVerticalGrid) {
            drawResults(ctx, shape);
          }
          // Draw border last so it sits over text.
          ctx.save();
          ctx.beginPath();
          ctx.rect(0, 0, shape.width(), shape.height());
          ctx.lineWidth = getScaledLineWidth(0.12, 0.001);
          ctx.strokeStyle = props.grid.color || "darkblue";
          ctx.globalAlpha = props.grid.opacity || 1;
          ctx.stroke();
          ctx.restore();
          // Allow Konva to apply hit detection
          ctx.fillStrokeShape(shape);
        }}
        onTap={() => props.selectGrid(props.i)}
        onClick={() => props.selectGrid(props.i)}
        onDragEnd={(e) => {
          const worldX = toWorldX(e.target.x() / scaleX);
          const worldY = toWorldY(e.target.y() / scaleY);
          props.updateGrid(e, props.i, false, undefined, undefined, undefined, { x: worldX, y: worldY });
        }}
      />
      {/* ) : (
        <Line
          ref={shapeRef}
          points={[
            Number(props.grid.origin.x.start) * props.gridScaleX * props.dxfScale,
            Number(props.grid.origin.y.start) * props.gridScaleY * props.dxfScale,
            Number(props.grid.origin.x.start + props.grid.origin.x.length) * props.gridScaleX * props.dxfScale,
            Number(props.grid.origin.y.start) * props.gridScaleY * props.dxfScale
          ]}
          x={0}
          y={0}
          rotation={props.grid.angle || 0}
          closed={false}
          stroke={props.grid.color || "darkblue"}
          strokeWidth={Math.max(0.3, 2 / props.stageScale)}
          listening={!props.grid.locked}
          draggable={props.selected}
          // draggableCursor="default"
          onTap={() => props.selectGrid(props.i)}
          onClick={() => props.selectGrid(props.i)}
          onDragEnd={(e) => props.updateVerticalGrid(e, props.grid.id)}
        />
      )} */}

      {/* {props.selected && props.grid.type !== 'Vertical' && ( */}
      <Transformer
        ref={trRef}
        enabledAnchors={
          String(props.grid?.type || "").toLowerCase() === "vertical"
            ? ["middle-left", "middle-right"]
            : undefined
        }
        onTransformEnd={(e) => {
          const node = trRef.current.nodes()[0];
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          const visualWidth = node.width() * scaleX;
          const isVerticalGrid = String(props.grid?.type || "").toLowerCase() === "vertical";
          const visualHeight = isVerticalGrid ? node.height() : (node.height() * scaleY);

          const newRotation = node.rotation();
          node.scaleX(1);
          node.scaleY(1);

          const worldX = toWorldX(node.x() / (props.gridScaleX * props.dxfScale));
          const worldY = toWorldY(node.y() / (props.gridScaleY * props.dxfScale));
          props.updateGrid(e, props.i, true, visualWidth, visualHeight, newRotation, { x: worldX, y: worldY });
        }}
      />
      {/* )}
      {props.selected && props.grid.type === 'Vertical' && (
        <Transformer
          ref={trRef}
          nodes={shapeRef.current ? [shapeRef.current] : []}
          onTransformEnd={(e) => {
            const node = shapeRef.current;
            const scaleX = node.scaleX();
            const visualWidth = node.width() * scaleX;
            const newRotation = node.rotation();
            node.scaleX(1); // reset scale so future transforms work correctly

            props.updateVerticalGrid(e, props.grid.id, visualWidth, newRotation);

            // Reset node position so it doesn't accumulate offset
            node.x(0);
            node.y(0);
          }}
        />
      )} */}
    </Layer>
  ) : null;
} 
